// ============================================
// FILE: src/components/AuthorizedPartnerPage.jsx
// ============================================

import React, { useRef, useEffect, useState } from 'react';
import { Handshake, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

// Pune Builders Partner Data - Only 4 partners with local logo images
const partners = [
  {
    id: 1,
    name: "Pride Group",
    logo: "/images/pride.webp"
  },
  {
    id: 2,
    name: "Nyati Group",
    logo: "/images/nyati.png"
  },
  {
    id: 3,
    name: "Goal Ganga group",
    logo: "/images/goal_ganga.png"
  },
  {
    id: 4,
    name: "Choice Group",
    logo: "/images/choicegrouppune_logo.jpeg"
  },
   {
    id: 5,
    name: "Kolte-patil group",
    logo: "/images/kolte_logo.jpeg"
  }
];

const PartnerCard = ({ partner }) => {
  const [imgError, setImgError] = useState(false);

  return (
    <div className="flex-shrink-0 w-[200px] mx-3">
      <div className="group bg-white rounded-2xl p-5 shadow-md hover:shadow-xl transition-all duration-300 ease-out cursor-pointer border border-purple-200 hover:border-purple-400 h-full">
        <div className="text-center">
          {/* Logo with image */}
          <div className="w-20 h-20 mx-auto flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
            {!imgError ? (
              <img 
                src={partner.logo} 
                alt={partner.name}
                className="w-full h-full object-contain"
                onError={() => setImgError(true)}
              />
            ) : (
              <span className="text-5xl">🏗️</span>
            )}
          </div>

          {/* Name */}
          <h3 
            className="text-base font-semibold text-gray-800 group-hover:text-purple-600 transition-colors duration-300"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {partner.name}
          </h3>
        </div>
      </div>
    </div>
  );
};

const AuthorizedPartnerPage = () => {
  const scrollContainerRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);

  // Auto-scroll functionality
  useEffect(() => {
    if (isHovering) return;
    
    const interval = setInterval(() => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        
        if (container.scrollLeft >= maxScrollLeft - 10) {
          // Reset to start seamlessly
          container.scrollTo({
            left: 0,
            behavior: 'smooth'
          });
        } else {
          const scrollAmount = 215; // Width of one card including margin
          container.scrollTo({
            left: container.scrollLeft + scrollAmount,
            behavior: 'smooth'
          });
        }
      }
    }, 3000); // Auto move every 3 seconds

    return () => clearInterval(interval);
  }, [isHovering]);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = 430; // Width of two cards
      
      if (direction === 'left') {
        container.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth'
        });
      } else {
        container.scrollBy({
          left: scrollAmount,
          behavior: 'smooth'
        });
      }
    }
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 via-purple-50/50 to-white py-12">
      <div className="container mx-auto px-4">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="inline-block mb-3">
            <span 
              className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold bg-purple-100 text-purple-700"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <Handshake size={14} className="mr-2" />
              Our Partners
            </span>
          </div>
          
          <h2 
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Authorized Channel Partners
          </h2>
          
          <div className="w-20 h-0.5 bg-gradient-to-r from-purple-400 to-purple-600 mx-auto mb-4 rounded-full"></div>
          
          <p 
            className="text-gray-600 max-w-2xl mx-auto text-base"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            We're proud to work with Pune's most trusted real estate builders and developers
          </p>
        </div>

        {/* Carousel Section */}
        <div 
          className="relative max-w-7xl mx-auto"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {/* Left Navigation Button */}
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-300 text-purple-600 hover:text-purple-700"
          >
            <ChevronLeft size={24} />
          </button>

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto scroll-smooth gap-4 py-4 px-2 hide-scrollbar"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {partners.map((partner) => (
              <PartnerCard key={partner.id} partner={partner} />
            ))}
          </div>

          {/* Right Navigation Button */}
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-300 text-purple-600 hover:text-purple-700"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      </div>

      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default AuthorizedPartnerPage;